// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import seoFieldDefinitions from "@keller/App/Widgets/SearchEngineOptimization/Fields";
import { SeoAppProps, SeoHandlerProps } from "@keller/App/Widgets/SearchEngineOptimization/Type";
import { WithContext } from "@keller/App/Widgets/SearchEngineOptimization/Type/schema";
import safeJsonLdReplacer from "@keller/App/Widgets/SearchEngineOptimization/Utilities/safeJsonLdReplacer";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

// Extensions
import Brand from "@keller/App/Widgets/SearchEngineOptimization/Schema/SchemaBrand";
import BreadcrumbList from "@keller/App/Widgets/SearchEngineOptimization/Schema/SchemaBreadcrumbList";
import CollectionPage from "@keller/App/Widgets/SearchEngineOptimization/Schema/SchemaCollectionPage";
import ContactPage from "@keller/App/Widgets/SearchEngineOptimization/Schema/SchemaContactPage";
import LocalBusiness from "@keller/App/Widgets/SearchEngineOptimization/Schema/SchemaLocalBusiness";
import Organization from "@keller/App/Widgets/SearchEngineOptimization/Schema/SchemaOrganization";
import Product from "@keller/App/Widgets/SearchEngineOptimization/Schema/SchemaProduct";
import SiteNavigationElement from "@keller/App/Widgets/SearchEngineOptimization/Schema/SchemaSiteNavigationElement";
import WebPage from "@keller/App/Widgets/SearchEngineOptimization/Schema/SchemaWebPage";
import Website from "@keller/App/Widgets/SearchEngineOptimization/Schema/SchemaWebSite";
import { shallowEqual, useStore } from "react-redux";

interface OwnProps extends WidgetProps {
    fields: SeoAppProps["fields"];
}

interface SeoGeneratorStyles {
    container?: InjectableCss;
}

const seoGeneratorStyles: SeoGeneratorStyles = {
    container: {
        css: css`
            background: #e5e5e5;
            padding: 10px;
        `,
    },
};

const styles = seoGeneratorStyles;
const SeoGenerator: FC<OwnProps> = ({ fields }) => {
    const { isInShell } = useShellContext();
    const allProductsPageUrl = useGetLink(fields.allProductsPage)?.url;

    const currentPage = useAppSelector(state => getCurrentPage(state), shallowEqual);
    const url = useAppSelector(state => `https://${state?.context?.website?.uri?.split("/")[2].split(":")[0]}`);

    // Read the full state from the store directly instead of via a subscribing selector. Selecting the
    // entire root state through useAppSelector triggers a react-redux warning and unnecessary rerenders.
    // The component already subscribes to the slices it depends on (currentPage, url) above.
    const store = useStore();

    const props: SeoAppProps = useMemo(
        () => ({
            state: store.getState() as SeoAppProps["state"],
            currentPage,
            url,
            payload: { allProductsPageUrl },
            fields,
        }),
        [store, currentPage, url, allProductsPageUrl, fields],
    );

    const build = (extension: SeoHandlerProps) =>
        !!extension?.isActivate(props) && <JsonLd json={extension?.buildSchema(props)} />;

    if (isInShell) {
        return (
            <StyledWrapper {...styles.container} data-test-selectore="SearchEngineOptimization">
                {translate("SearchEngineOptimization Widget")}
            </StyledWrapper>
        );
    }

    return (
        <>
            {build(Organization)}
            {build(Website)}
            {build(WebPage)}
            {build(ContactPage)}
            {build(LocalBusiness)}
            {build(BreadcrumbList)}
            {build(SiteNavigationElement)}
            {build(CollectionPage)}
            {build(Product)}
            {build(Brand)}
        </>
    );
};

const JsonLd: FC<{ json: WithContext<any> }> = ({ json }) => (
    <script type="application/ld+json">{JSON.stringify(json, safeJsonLdReplacer)}</script>
);

const widgetModule: WidgetModule = {
    component: SeoGenerator,
    definition: {
        group: "Xngage" as any,
        displayName: "Search Engine Optimization",
        allowedContexts: ["Header", "SharedContent"],
        fieldDefinitions: [...seoFieldDefinitions],
    },
};

export default widgetModule;
